import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Pause, Timer } from "lucide-react";
import { cn } from "@weco/ui";

import { MANUAL_STATUSES, type ManualStatus, type OfficeStatus, type TimerSnapshot } from "../modules/homework/api/types";
import { useMe, useSetStatus, useTimer } from "../modules/homework/hooks/useOffice";
import { STATUS_LABEL_KEYS } from "../modules/homework/lib/format";
import { useOfficeEntered } from "../modules/homework/lib/officeSession";
import { useOfficeRealtime } from "../modules/homework/realtime/OfficeRealtimeProvider";

/**
 * Header work status (design: `weco2/work-status.jsx` WorkStatusBar).
 *
 * Backed by HomeWork's real presence and time tracking, not the mock's
 * localStorage stopwatch: the session/today/week numbers are the office
 * service's timer, and choosing a status goes through the same
 * `useSetStatus` mutation the HomeWork module uses. The live presence frame
 * wins over the REST copy, exactly as in HomeWorkLayout.
 *
 * Shown only once this tab has entered HomeWork. Before that the user is not
 * on the floor (the socket stays shut, see lib/officeSession), so the server
 * would report them offline and a status picked here could not take effect.
 */

interface StatusStyle {
  /** Saturated dot. */
  dot: string;
  /** Low-alpha wash behind the timer pill. */
  wash: string;
  /** Inset ring around the timer pill. */
  ring: string;
  /** Readable ink in both themes. */
  ink: string;
}

const STATUS_STYLE: Record<OfficeStatus, StatusStyle> = {
  available: { dot: "#10b981", wash: "rgba(16,185,129,.12)", ring: "rgba(16,185,129,.28)", ink: "text-emerald-800 dark:text-emerald-300" },
  focus: { dot: "#8b5cf6", wash: "rgba(139,92,246,.12)", ring: "rgba(139,92,246,.28)", ink: "text-violet-800 dark:text-violet-300" },
  break: { dot: "#06b6d4", wash: "rgba(6,182,212,.12)", ring: "rgba(6,182,212,.28)", ink: "text-cyan-800 dark:text-cyan-300" },
  away: { dot: "#94a3b8", wash: "rgba(148,163,184,.14)", ring: "rgba(148,163,184,.4)", ink: "text-slate-700 dark:text-slate-300" },
  meeting: { dot: "#f59e0b", wash: "rgba(245,158,11,.12)", ring: "rgba(245,158,11,.3)", ink: "text-amber-800 dark:text-amber-300" },
  offline: { dot: "#94a3b8", wash: "rgba(148,163,184,.10)", ring: "rgba(148,163,184,.3)", ink: "text-muted-foreground" },
};

/** Short button labels where the menu label is a sentence. */
const SHORT_LABEL_KEYS: Partial<Record<OfficeStatus, string>> = {
  focus: "shell.workStatus.short.focus",
  break: "shell.workStatus.short.break",
};

const DESC_KEYS: Record<ManualStatus, string> = {
  available: "shell.workStatus.desc.available",
  focus: "shell.workStatus.desc.focus",
  break: "shell.workStatus.desc.break",
  away: "shell.workStatus.desc.away",
};

const DESC_DEFAULTS: Record<ManualStatus, string> = {
  available: "Teammates can walk over and talk to you.",
  focus: "People have to knock and wait for you to accept.",
  break: "Your timer pauses and break time is tracked separately.",
  away: "Your timer pauses until you come back.",
};

/** "0h 05m" — the mock's compact clock, in the module's own units. */
function useClockFormat() {
  const { t } = useTranslation();
  return (seconds: number) => {
    const total = Math.max(0, Math.floor(seconds / 60));
    const h = Math.floor(total / 60);
    const m = String(total % 60).padStart(2, "0");
    return `${h}${t("homework.units.hours")} ${m}${t("homework.units.minutes")}`;
  };
}

/** The server's numbers, advanced locally between polls while not paused. */
function useLiveTotals(snapshot: TimerSnapshot | undefined) {
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  const [, tick] = useState(0);
  useEffect(() => {
    setReceivedAt(Date.now());
  }, [snapshot?.as_of, snapshot?.session_seconds]);
  useEffect(() => {
    if (!snapshot || snapshot.paused) return;
    const id = window.setInterval(() => tick((n) => n + 1), 15_000);
    return () => window.clearInterval(id);
  }, [snapshot]);
  if (!snapshot) return null;
  const add = snapshot.paused ? 0 : Math.max(0, (Date.now() - receivedAt) / 1000);
  return {
    session: snapshot.session_seconds + add,
    today: snapshot.today_seconds + add,
    week: snapshot.week_seconds + add,
    breakToday: snapshot.break_seconds_today + (snapshot.status === "break" ? add : 0),
    paused: snapshot.paused,
  };
}

export function WorkStatusBar({ compact = false, enabled = true }: { compact?: boolean; enabled?: boolean }) {
  const entered = useOfficeEntered();
  if (!enabled || !entered) return null;
  return <WorkStatusBarInner compact={compact} />;
}

function WorkStatusBarInner({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const fmt = useClockFormat();
  const { data: me, error } = useMe();
  const { data: timer } = useTimer();
  const setStatus = useSetStatus();
  const { people } = useOfficeRealtime();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", off);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", off);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const totals = useLiveTotals(timer ?? me?.timer);
  if (error || !me || !totals) return null;

  const presence = people[me.presence.user_id] ?? me.presence;
  const status: OfficeStatus = presence.status;
  const style = STATUS_STYLE[status];
  const label = t(STATUS_LABEL_KEYS[status]);
  const shortKey = SHORT_LABEL_KEYS[status];
  const shortLabel = shortKey ? t(shortKey, { defaultValue: label }) : label;
  const totalsText = t("shell.workStatus.totals", {
    defaultValue: "Today {{today}} · Week {{week}}",
    today: fmt(totals.today),
    week: fmt(totals.week),
  });
  const sessionText = t("shell.workStatus.thisSession", { defaultValue: "this session" });
  const dot = (color: string) => (
    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} aria-hidden />
  );

  return (
    <div ref={ref} className="relative flex items-center gap-2 shrink-0">
      {!compact && (
        <div
          className="wc-ws-timer flex items-center gap-2 rounded-full px-3"
          style={{ height: 35, background: style.wash, boxShadow: `inset 0 0 0 1px ${style.ring}` }}
          title={totalsText}
        >
          {totals.paused ? (
            <Pause size={15} className={style.ink} aria-hidden />
          ) : (
            <Timer size={15} className={style.ink} aria-hidden />
          )}
          <span className="font-num tabular-nums text-[13px] font-bold leading-none text-foreground">{fmt(totals.session)}</span>
          <span className="text-[11px] font-medium leading-none text-muted-foreground whitespace-nowrap hidden xl:inline">{totalsText}</span>
          <span className="text-[11px] font-medium leading-none text-muted-foreground whitespace-nowrap xl:hidden">{sessionText}</span>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("shell.workStatus.ariaLabel", { defaultValue: "Status: {{status}}", status: label })}
        disabled={setStatus.isPending}
        className="glass-bar flex items-center gap-2 rounded-full ps-3 pe-2 hover:bg-foreground/5 active:scale-95 transition-all disabled:opacity-60"
        style={{ height: compact ? 36 : 35 }}
      >
        {dot(style.dot)}
        {compact ? (
          <span className="font-num tabular-nums text-[12px] font-bold text-foreground">{fmt(totals.session)}</span>
        ) : (
          <span className={cn("text-[13px] font-semibold whitespace-nowrap", style.ink)}>{shortLabel}</span>
        )}
        <ChevronDown size={14} className={cn("text-muted-foreground transition-transform", open && "rotate-180")} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full mt-2 end-0 z-50 w-[300px] max-w-[calc(100vw-24px)] rounded-3xl border border-border bg-background shadow-2xl p-2"
        >
          {compact && (
            <div className="px-3 pt-2 pb-3 mb-1 border-b border-border">
              <p className="font-num tabular-nums text-base font-bold text-foreground leading-tight">
                {fmt(totals.session)} <span className="text-xs font-medium text-muted-foreground">{sessionText}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{totalsText}</p>
            </div>
          )}
          {MANUAL_STATUSES.map((option) => {
            const selected = option === status;
            return (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  if (!selected) setStatus.mutate({ status: option, message: presence.status_message || undefined });
                  setOpen(false);
                }}
                className={cn(
                  "w-full flex items-start gap-3 text-start rounded-2xl px-3 py-3 min-h-[44px] transition-colors",
                  selected ? "bg-foreground/5" : "hover:bg-foreground/5",
                )}
              >
                <span className="mt-2">{dot(STATUS_STYLE[option].dot)}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {t(STATUS_LABEL_KEYS[option])}
                    {selected && <Check size={14} className="text-foreground" aria-hidden />}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5 leading-snug">
                    {t(DESC_KEYS[option], { defaultValue: DESC_DEFAULTS[option] })}
                  </span>
                </span>
              </button>
            );
          })}
          {totals.breakToday > 60 && (
            <p className="px-3 pt-2 pb-1 text-[11px] text-muted-foreground border-t border-border mt-1">
              {t("shell.workStatus.breakToday", { defaultValue: "Break today {{time}}", time: fmt(totals.breakToday) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
